import { useCallback } from 'react'
import { YStack, XStack, isWeb, useMedia } from 'tamagui'
import { useTranslation } from 'react-i18next'
import { useGetProductCurriculum } from '@services/api/graphql/queries/getProductPage'
import { IProductPage } from '@dtos/ProductPage'
import { CONTAINER_WIDTH } from './constants'
import { usePricing } from './hooks'
import { Aside } from './Aside'
import { ComboCourses } from './ComboCourses'
import { CurriculumAccordion } from './CurriculumAccordion'
import { FeaturesRow } from './FeaturesRow'
import { FooterSection } from './FooterSection'
import { HeroSection } from './HeroSection'
import { HtmlContent } from './HtmlContent'
import { MobileBuyBar } from './MobileBuyBar'
import { SectionTitle } from './SectionTitle'
import { TeacherSection } from './TeacherSection'
import { TopicsList } from './TopicsList'
import { TrackingScripts } from './TrackingScripts'

interface IProductPageProps {
  product: IProductPage
  schoolUrl: string
}

// Página de vendas do produto: composição das seções do product.liquid do
// hero_theme (hero, corpo + aside, instrutor, features, rodapé).
export function ProductPage({ product, schoolUrl }: IProductPageProps) {
  const { t } = useTranslation('ProductPage')
  const media = useMedia()
  const { option } = usePricing(product)
  const { getProductCurriculum } = useGetProductCurriculum()

  const loadCurriculumPage = useCallback(
    async (page: number) => {
      const { data } = await getProductCurriculum({
        variables: { schoolUrl, slug: product.slug ?? '', page },
      })
      return data?.productPage?.course?.curriculum ?? null
    },
    [getProductCurriculum, schoolUrl, product.slug],
  )

  const isCombo = product.kind === 'path'
  const showCurriculum =
    product.course &&
    ['online_course', 'live_meet'].includes(product.course.kind ?? '')
  const hasMobileBar = isWeb && !media.gtSm && Boolean(option?.checkoutUrl)

  return (
    <YStack
      backgroundColor="$bg-page"
      minHeight="100%"
      width="100%"
      paddingBottom={hasMobileBar ? 96 : 0}
    >
      <TrackingScripts option={product.currentPaymentOption} />

      <HeroSection product={product} />

      <YStack
        width="100%"
        maxWidth={CONTAINER_WIDTH}
        alignSelf="center"
        paddingHorizontal={16}
        marginTop={64}
      >
        <XStack
          width="100%"
          gap={48}
          alignItems="flex-start"
          flexDirection={media.gtSm ? 'row' : 'column'}
        >
          <YStack flex={1} width={media.gtSm ? undefined : '100%'}>
            {product.description ? (
              <YStack marginBottom={64} testID="product-description">
                <SectionTitle>
                  {isCombo ? t('combo.about-title') : t('about.title')}
                </SectionTitle>
                <HtmlContent html={product.description} />
              </YStack>
            ) : null}

            <TopicsList product={product} />

            {showCurriculum && product.course && (
              <YStack width="100%">
                <SectionTitle>
                  {isCombo ? t('combo.content-title') : t('content.title')}
                </SectionTitle>
                <CurriculumAccordion
                  initialPage={product.course.curriculum}
                  onLoadMore={loadCurriculumPage}
                />
              </YStack>
            )}

            <ComboCourses product={product} />
          </YStack>

          <Aside product={product} />
        </XStack>

        <TeacherSection product={product} />
      </YStack>

      <YStack width="100%" alignItems="center">
        <FeaturesRow product={product} />
      </YStack>

      <FooterSection product={product} />

      <MobileBuyBar product={product} />
    </YStack>
  )
}
